import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}

export default function ModernTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (
    <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
      <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
        {/* Header - Dynamic Color */}
        <div className={`bg-gradient-to-r ${colors.headerBg} ${colors.headerText} p-8`}>
          <h1 className="text-4xl font-bold mb-3">
            {personalInfo.fullName || 'YOUR NAME'}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {personalInfo.email && (
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>{personalInfo.email}</span>
              </div>
            )}
            {personalInfo.phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{personalInfo.phone}</span>
              </div>
            )}
            {personalInfo.address && (
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{personalInfo.address}</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mt-2">
            {personalInfo.linkedin && (
              <div className="flex items-center space-x-2">
                <Linkedin className="w-4 h-4" />
                <span>{personalInfo.linkedin}</span>
              </div>
            )}
            {personalInfo.website && (
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>{personalInfo.website}</span>
              </div>
            )}
            {personalInfo.github && (
              <div className="flex items-center space-x-2">
                <Github className="w-4 h-4" />
                <span>{personalInfo.github}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-8">
          {/* Summary */}
          {summary && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.professionalSummary}
              </h2>
              <p className="text-gray-700 leading-relaxed text-justify">{summary}</p>
            </div>
          )}

          {/* Experience */}
          {experience && experience.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.workExperience}
              </h2>
              <div className="space-y-5">
                {experience.map((exp: any) => (
                  <div key={exp.id} className={`relative pl-6 border-l-4 ${colors.timelineBorder}`}>
                    <div className={`absolute w-3 h-3 ${colors.timelineDot} rounded-full -left-[9px] top-1`}></div>
                    <div className="mb-2">
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{exp.jobTitle}</h3>
                          <div className={`${colors.accentText} font-semibold`}>{exp.company}</div>
                        </div>
                        <div className="text-sm text-gray-600 flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{exp.startDate} - {exp.endDate || labels.present}</span>
                        </div>
                      </div>
                      {exp.location && (
                        <div className="text-sm text-gray-600 flex items-center space-x-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{exp.location}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {exp.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {skills && skills.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.skills}
              </h2>
              <div className="grid grid-cols-1 gap-3">
                {skills.map((skillGroup: any, index: number) => (
                  <div key={index} className="flex items-start">
                    <div className="font-bold text-gray-900 min-w-[180px]">{skillGroup.category}:</div>
                    <div className="text-gray-700 flex-1">
                      {skillGroup.skills?.join(' • ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {education && education.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.education}
              </h2>
              <div className="space-y-4">
                {education.map((edu: any) => (
                  <div key={edu.id}>
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{edu.degree}</h3>
                        <div className="text-gray-700 font-medium">{edu.institution}</div>
                        {edu.location && (
                          <div className="text-sm text-gray-600">{edu.location}</div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {edu.graduationDate}
                        {edu.gpa && <span className="ml-2 font-semibold">| GPA: {edu.gpa}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects */}
          {projects && projects.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.projects}
              </h2>
              <div className="space-y-4">
                {projects.map((project: any) => (
                  <div key={project.id}>
                    <h3 className="text-lg font-bold text-gray-900">{project.name}</h3>
                    <p className="text-gray-700 mt-1 leading-relaxed">{project.description}</p>
                    {project.technologies && project.technologies.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {project.technologies.map((tech: string, i: number) => (
                          <span
                            key={i}
                            className={`px-3 py-1 ${colors.badgeBg} ${colors.badgeText} text-xs font-medium rounded-full`}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {certifications && certifications.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder} uppercase tracking-wide`}>
                {labels.certifications}
              </h2>
              <div className="space-y-3">
                {certifications.map((cert: any) => (
                  <div key={cert.id} className="flex items-start space-x-3">
                    <Award className={`w-5 h-5 ${colors.accentText} flex-shrink-0 mt-1`} />
                    <div>
                      <h3 className="font-bold text-gray-900">{cert.name}</h3>
                      <div className="text-sm text-gray-600">
                        {cert.issuer} | {cert.date}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
